import React, { useState, useRef, useCallback } from 'react';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  acceptedTypes: string[];
  maxSize: number;
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  multiple?: boolean;
}

export function FileUpload({
  onFileSelect,
  acceptedTypes,
  maxSize,
  loading = false,
  error,
  disabled = false,
  multiple = false,
}: FileUploadProps): JSX.Element {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    const isValidType = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type.substring(1);
      }
      return mimeType === type || mimeType.startsWith(type.split('/')[0] + '/');
    });

    if (!isValidType) {
      return `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`;
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      return `File size exceeds ${maxSizeMB}MB limit`;
    }

    return null;
  }, [acceptedTypes, maxSize]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0]; // Take first file even if multiple is true
    const validationError = validateFile(file);
    
    if (validationError) {
      // You could show this error via toast or pass it up
      console.error('File validation error:', validationError);
      return;
    }

    onFileSelect(file);
  }, [onFileSelect, validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !loading) {
      setIsDragOver(true);
    }
  }, [disabled, loading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled || loading) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [disabled, loading, handleFileSelect]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  const handleClick = useCallback(() => {
    if (!disabled && !loading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, loading]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const dropZoneClass = `
    border-2 border-dashed rounded p-4 text-center cursor-pointer transition-all
    ${isDragOver ? 'border-primary bg-light' : 'border-secondary'}
    ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary hover:bg-light'}
    ${error ? 'border-danger' : ''}
  `.trim();

  return (
    <div className="file-upload-container">
      <div
        className={dropZoneClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          disabled={disabled || loading}
          multiple={multiple}
        />
        
        {loading ? (
          <div className="py-4">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Uploading...</span>
            </div>
            <p className="mb-0 text-muted">Uploading file...</p>
          </div>
        ) : (
          <div className="py-4">
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h5 className="mb-2">
              {isDragOver ? 'Drop file here' : 'Drag & drop file here'}
            </h5>
            <p className="text-muted mb-2">or click to browse</p>
            <small className="text-muted">
              Accepted: {acceptedTypes.join(', ')} • Max size: {formatFileSize(maxSize)}
            </small>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger mt-2 mb-0">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {error}
        </div>
      )}
    </div>
  );
}